"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import api from "@/lib/axios";
// @ts-ignore
import Cookies from "js-cookie";
import { identifyUser } from "@/lib/posthog";
import { Account } from "@/services/account-service";

interface Creator {
  country_of_residence: string;
  spaces: Space[];
}

interface Favorite {
  id: number;
  uuid: string;
  type: string;
  emoji: string;
  name: string;
}

interface User {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  referral_source: string;
  email: string;
  avatar: string;
  newsletter: boolean;
  subscriptions: Subscription[];
  is_subscribed: boolean;
  is_onboarded: boolean;
  onboarding_step: number;
  country: string;
  type: string;
  creator: Creator;
  favorites: Favorite[];
  is_admin: boolean;
  is_impersonating: boolean;
}

interface Subscription {
  type: string;
  stripe_status: string;
}

interface Space {
  uuid: string;
  name: string;
  icon: string;
  pivot: SpacePivot;
  owner: User;
  website: string;
  creators: string;
  type: string;
  tier: string;
  activeCampaigns: number;
  accounts: number;
}

interface Invitation {
  space: Space;
  uuid: string;
  email: string;
  sender: User;
}

interface SpacePivot {
  is_owner: boolean;
  role: string;
}

interface UserContextType {
  user: User | null;
  loading: boolean;
  spaces: Space[] | null;
  invitations: Invitation[] | null;
  setInvitations: React.Dispatch<React.SetStateAction<Invitation[] | null>>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  setSpaces: React.Dispatch<React.SetStateAction<Space[] | null>>;
  activeSpace?: Space;
  logout: () => Promise<void>;
  setActiveSpace: (space: Space | null) => void;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [spaces, setSpaces] = useState<Space[] | null>(null);
  const [activeSpace, setActiveSpace] = useState<Space>();
  const [invitations, setInvitations] = useState<Invitation[] | null>(null);

  useEffect(() => {
    const initializeUser = async () => {
      try {
        setLoading(true);
        const loggedInCookie = document.cookie.includes("loggedIn");

        if (!loggedInCookie) {
          return;
        }

        const response = await api.get("/auth/me");

        if (response.status === 401) {
          return;
        }

        if (response.data.user) {
          setUser(response.data.user);
          // Identify user in PostHog when logged in
          identifyUser(response.data.user.id, {
            email: response.data.user.email,
            name: response.data.user.name,
            type: response.data.user.type,
          });
          setSpaces(
            response.data.user.type === "business"
              ? response.data.user.spaces
              : response.data.user.creator.spaces
          );
          setInvitations(response.data.user.invitations);
        }
      } catch (error) {
        console.log("failed to fetch user.");
        //console.error("Failed to fetch user:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, []);

  useEffect(() => {
    if (spaces?.length) {
      const savedSpaceUuid = localStorage.getItem("activeSpaceUuid");
      const savedSpace = spaces.find((space) => space.uuid === savedSpaceUuid);
      setActiveSpace(savedSpace || spaces[0]);
    }
  }, [spaces]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
      // Reset PostHog user identification on logout
      if (typeof window !== "undefined" && (window as any).posthog) {
        (window as any).posthog.reset();
      }
      setUser(null);
      setSpaces(null);
      setActiveSpace(undefined);
      localStorage.removeItem("activeSpaceUuid");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      if (response.data.user) {
        setUser(response.data.user);
        setSpaces(response.data.user.spaces);
        setActiveSpace(response.data.user.spaces[0]);
        setInvitations(response.data.user.invitations);
        identifyUser(response.data.user.id, {
          email: response.data.user.email,
          name: response.data.user.name,
          type: response.data.user.type,
        });
      }
    } catch (error) {
      // console.error("Failed to fetch user:", error);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        setUser,
        spaces,
        setSpaces,
        activeSpace,
        setActiveSpace: (space: Space | null) => setActiveSpace(space as any),
        logout,
        refreshUser,
        invitations,
        setInvitations,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
